import { Button, Tooltip } from "@may-i/mash-design-system";
import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

export interface ValidationItem {
  message: string;
  values: string[];
}

export interface ValidationResult {
  errors: ValidationItem[];
  warnings: ValidationItem[];
}

interface DraftActionBarProps {
  onSave?: () => void;
  onSubmit?: () => void;
  isSaving?: boolean;
  hasValidationError?: boolean;
  validationResult?: ValidationResult;
  // 8차: 도면/스냅샷 좌표 편집 중이면 저장/제출 차단 (미커밋 좌표 저장 → 같은 key 두 라인 방지)
  isCoordinateEditing?: boolean;
  /** 저장할 변경사항이 있는지 (F8). false 면 '저장'만 비활성. '제출'은 변경 없어도 동작해야 하므로 영향 없음 */
  hasChanges?: boolean;
}

export const DraftActionBar = forwardRef<HTMLDivElement, DraftActionBarProps>(function DraftActionBar(
  { onSave, onSubmit, isSaving, hasValidationError, validationResult, isCoordinateEditing, hasChanges = true },
  ref,
) {
  const { t } = useTranslation("analysis-draft");
  const disabled = isSaving || hasValidationError || isCoordinateEditing;
  // F8: 변경 없으면 '저장'만 비활성. '제출'은 변경 없어도 동작(Slack 알림) — disabled 그대로 사용
  const saveDisabled = disabled || !hasChanges;
  const hasErrors = (validationResult?.errors.length ?? 0) > 0;
  const hasWarnings = (validationResult?.warnings.length ?? 0) > 0;
  const showTooltip = hasErrors || hasWarnings;

  const saveButton = (
    <Button
      variant="outlined"
      color="primary"
      size="small"
      onClick={onSave}
      disabled={saveDisabled}
    >
      {t("action.save")}
    </Button>
  );

  const submitButton = (
    <Button
      variant="contained"
      color="primary"
      size="small"
      onClick={onSubmit}
      disabled={disabled}
    >
      {/* F8: 제출은 내부적으로 저장을 먼저 수행 → 변경 있으면 '저장 후 제출'로 명시 */}
      {hasChanges ? t("action.saveAndSubmit") : t("action.submit")}
    </Button>
  );

  // 좌표 편집 중이면 그 안내를 우선 노출 (검증 tooltip 보다 우선 — 버튼 비활성 사유가 편집이므로)
  if (isCoordinateEditing) {
    const editTooltip = <TooltipBody>{t("tooltip.coordinateEditing")}</TooltipBody>;
    return (
      <Container ref={ref}>
        <Tooltip trigger={<span>{saveButton}</span>} side="top" sideOffset={16}>
          {editTooltip}
        </Tooltip>
        <Tooltip trigger={<span>{submitButton}</span>} side="top" sideOffset={16}>
          {editTooltip}
        </Tooltip>
      </Container>
    );
  }

  if (showTooltip) {
    const tooltipContent = (
      <TooltipBody>
        {hasErrors && (
          <>
            <SectionLabel $color="#FA332D">{t("tooltip.errorLabel")}</SectionLabel>
            <TooltipList>
              {validationResult!.errors.map((item) => (
                <li key={item.message}>
                  {t(item.message)}
                  {item.values.length > 0 && (
                    <ValuesText> : {item.values.join(", ")}</ValuesText>
                  )}
                </li>
              ))}
            </TooltipList>
          </>
        )}
        {hasWarnings && (
          <>
            <SectionLabel $color="#FFD600">{t("tooltip.warningLabel")}</SectionLabel>
            <TooltipList>
              {validationResult!.warnings.map((item) => (
                <li key={item.message}>
                  {t(item.message)}
                  {item.values.length > 0 && (
                    <ValuesText> : {item.values.join(", ")}</ValuesText>
                  )}
                </li>
              ))}
            </TooltipList>
          </>
        )}
      </TooltipBody>
    );

    return (
      <Container ref={ref}>
        <Tooltip trigger={<span>{saveButton}</span>} side="top" sideOffset={16}>
          {tooltipContent}
        </Tooltip>
        <Tooltip trigger={<span>{submitButton}</span>} side="top" sideOffset={16}>
          {tooltipContent}
        </Tooltip>
      </Container>
    );
  }

  return (
    <Container ref={ref}>
      {saveButton}
      {submitButton}
    </Container>
  );
});

// --- Styles ---

const Container = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.12);
  border-radius: 8px 8px 0 0;
  z-index: 100;
`;

const TooltipBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 360px;
`;

const SectionLabel = styled.span<{ $color: string }>`
  font-weight: 700;
  font-size: 12px;
  line-height: 1;
  color: ${({ $color }) => $color};
  text-decoration: underline;
  text-underline-offset: 2px;
`;

const TooltipList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.3;

  li::before {
    content: "·";
    margin-right: 4px;
  }
`;

const ValuesText = styled.span`
  opacity: 0.8;
`;
